// Per-user so a shared browser doesn't leak one account's selection into
// another's (the Firebase path keyed selection per uid too). The value is
// validated against the live membership list on bootstrap (pickActiveOrg) and
// again server-side by ActiveOrgSelectionMiddleware, so a stale id is harmless —
// but it should not be shared between accounts.
const ACTIVE_ORG_STORAGE_PREFIX = 'mp.activeOrgId';

export function activeOrgStorageKey(userId: string): string {
  return `${ACTIVE_ORG_STORAGE_PREFIX}.${userId}`;
}

export function readStoredActiveOrgId(userId: string | null): string | null {
  if (!userId) return null;
  try {
    return localStorage.getItem(activeOrgStorageKey(userId));
  } catch {
    return null;
  }
}

export function persistActiveOrgId(userId: string | null, orgId: string | null): void {
  if (!userId) return;
  try {
    const key = activeOrgStorageKey(userId);
    if (orgId) localStorage.setItem(key, orgId);
    else localStorage.removeItem(key);
  } catch {
    /* storage unavailable (private mode / quota) — selection just won't persist */
  }
}

export interface ActiveOrgCandidate {
  id: string;
  plan?: string | null;
  /** `supplier` = reached through a supplier access grant; absent on an API that predates it. */
  relationship?: string | null;
}

/**
 * An `org_not_available` answer only counts for the org the request was made for AND only while
 * that org is still the active one: a late 403 from the org the user just left must not flag the
 * new, healthy org as "access removed".
 */
export function shouldFlagOrgNotAvailable(requestOrg: string | null, activeOrg: string | null): boolean {
  return requestOrg !== null && requestOrg === activeOrg;
}

export function isSupplierRow(org: Pick<ActiveOrgCandidate, 'relationship'>): boolean {
  return org.relationship?.toLowerCase() === 'supplier';
}

export function isSupplierPlan(plan?: string | null): boolean {
  return plan?.toLowerCase() === 'supplier';
}

/**
 * Decides which org to make active on bootstrap. Pure so the restore/fallback/
 * clear matrix is unit-testable:
 *  - no memberships              → null (caller clears storage + shows onboarding)
 *  - a still-valid stored choice → that id (survives reload) — except a supplier
 *    row (an account manager inside a customer), which is never restored: a fresh
 *    session starts at the supplier org
 *  - else a Supplier-plan org the user is a member of → that org
 *  - else the first membership that is not a supplier row
 *  - only supplier rows left     → null (a supplier row is never a landing)
 */
export function pickActiveOrg(
  orgs: ReadonlyArray<ActiveOrgCandidate>,
  storedId: string | null,
): string | null {
  if (orgs.length === 0) return null;
  const landable = orgs.filter((o) => !isSupplierRow(o));
  if (storedId && landable.some((o) => o.id === storedId)) return storedId;
  const supplierOrg = landable.find((o) => isSupplierPlan(o.plan));
  if (supplierOrg) return supplierOrg.id;
  return landable[0]?.id ?? null;
}
